import get from 'lodash/get'
import { useMemo } from 'react'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'

import { getNewUuid } from '@avantstay/backoffice-core'

import {
  Chunk,
  DragAndDropStatusProps,
  IDragAndDropResultProps,
  IDragAndDropWrapperProps,
} from '../_types'
import { DragAndDropItem } from './DragAndDropItem'
import * as S from './DragAndDropWrapper.styles'

function mapAndInvoke<ItemType extends DragAndDropStatusProps>(
  onDragEnd: (result: IDragAndDropResultProps) => void,
  chunks: Chunk<ItemType>[],
  itemsPerLine: number,
) {
  return function ({ source, destination }: DropResult): void {
    if (destination === null || destination === undefined) {
      return
    }

    const destinationChunk = chunks.find(chunk => chunk.id === destination.droppableId)

    if (!destinationChunk) {
      return
    }

    const rowIndex = chunks.findIndex(chunk => chunk.id === destination.droppableId)

    const itemIndex =
      rowIndex === 0 ? destination.index : destination.index - rowIndex * itemsPerLine

    const item = destinationChunk.items[itemIndex]

    if (item?.disabled) {
      return
    }

    const result: IDragAndDropResultProps = {
      source: {
        id: source.droppableId,
        index: source.index,
      },
      destination: {
        id: destination.droppableId,
        index: destination.index,
      },
    }
    onDragEnd(result)
  }
}

export function DragAndDropWrapper<ItemType extends DragAndDropStatusProps>({
  onDragEnd,
  chunks,
  direction,
  render,
  itemsPerLine,
}: IDragAndDropWrapperProps<ItemType>) {
  const hash = useMemo(() => getNewUuid(), [])

  return (
    <DragDropContext onDragEnd={mapAndInvoke(onDragEnd, chunks, itemsPerLine)}>
      {chunks.map(({ id: droppableId, items }, rowIndex: number) => (
        <Droppable key={`${droppableId}-${hash}`} droppableId={droppableId} direction={direction}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={direction === 'horizontal' ? S.horizontalStyle : undefined}
              {...provided.droppableProps}
            >
              <S.PlaceholderContainer isDraggingOver={snapshot.isDraggingOver}>
                {items.map((_, key) => (
                  <S.Placeholder key={`drag-drop-placeholder-${key}`} />
                ))}
              </S.PlaceholderContainer>
              {items.map((item, index) => {
                const itemIndex = rowIndex === 0 ? index : rowIndex * itemsPerLine + index
                const itemId = get(item, 'id', itemIndex)

                return (
                  <DragAndDropItem
                    key={`${hash}-${itemId}`}
                    item={item}
                    index={itemIndex}
                    render={render}
                  />
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ))}
    </DragDropContext>
  )
}
